import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const STORAGE_KEY = 'inkvaluator-theme';

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
  document.body.setAttribute('data-theme', theme);
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

function getInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  return 'dark';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [userId, setUserId] = useState<string | null>(null);

  // Apply on mount & changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Safety net: re-apply on every render to prevent stale data-theme after navigation
  useEffect(() => {
    applyTheme(theme);
  });

  // Listen for auth state and load profile preference
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id ?? null;
      setUserId(uid);
      if (uid) {
        supabase
          .from('artist_profiles')
          .select('theme_preference')
          .eq('user_id', uid)
          .maybeSingle()
          .then(({ data }) => {
            const pref = (data as any)?.theme_preference;
            if (pref === 'dark' || pref === 'light') {
              // Always apply, even if same as current state
              setThemeState(pref);
              localStorage.setItem(STORAGE_KEY, pref);
              applyTheme(pref);
            }
          });
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem(STORAGE_KEY, t);
    applyTheme(t);
    if (userId) {
      supabase
        .from('artist_profiles')
        .update({ theme_preference: t } as any)
        .eq('user_id', userId)
        .then(() => {});
    }
  }, [userId]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
